import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  Alert
} from "reactstrap";
import mixpanelService from "common/mixpanel-service";
import pageData from "common/page-data";
import { fetchEnv } from "common/features/env/env-selectors";
import pathsService from "common/services/paths-service";
import StripeElementsProvider from "common/stripe/stripe-elements-provider";
import PaymentInformationSection from "../payment-information-section";
import StripeModalSubmitButton from "../stripe-modal-submit-button";
import { selectStripeRetailEnabled } from "../../flippers-selector";
import { selectAccount } from "../../account/account-selectors";
import creditCardIconSet from "../../settings/credit-card-icon-set.png";
import creditCardIconSet2x from "../../settings/credit-card-icon-set@2x.png";
import { useChangeCampaignPaymentMutation } from "../campaigns-api-endpoints";

const images = { cards: creditCardIconSet, cards2x: creditCardIconSet2x };

const INITIAL_FORM_STATE = {
  creditCardNumber: "",
  expirationMonth: "",
  expirationYear: "",
  cvv: "",
  zipCode: "",
  transactionId: -1,
  applyToAllActiveCampaigns: "false"
};

const propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  editBillingInfoLink: PropTypes.string.isRequired,
  campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  campaignType: PropTypes.string
};

const EditBillingInfoModal = ({
  isOpen,
  onClose,
  editBillingInfoLink,
  campaignId = null,
  campaignType = null
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [formErrors, setFormErrors] = useState([]);
  const [displaySuccess, setDisplaySuccess] = useState(false);

  const updateFormData = update => setFormData(prev => ({ ...prev, ...update }));

  const closeModal = () => {
    setFormData(INITIAL_FORM_STATE);
    setFormErrors([]);
    setDisplaySuccess(false);
    onClose();
  };

  const [changeCampaignPayment, { isLoading }] = useChangeCampaignPaymentMutation();

  const account = useSelector(selectAccount);
  const stripeRetailEnabled = useSelector(selectStripeRetailEnabled);
  const stripePublishableKey = useSelector(fetchEnv("stripePublishableKey"));
  const stripePaymentMethodsConfigId = useSelector(fetchEnv("stripePaymentMethodsConfigId"));
  const customerSessionPath = stripeRetailEnabled
    ? pathsService.build(pathsService.checkoutCustomerSessionPath, { accountId: account.id })
    : null;

  const submitPaymentChange = (stripeFields = {}) =>
    changeCampaignPayment({
      saveUrl: editBillingInfoLink,
      paymentDetails: formData,
      applyToAllActiveCampaigns: formData.applyToAllActiveCampaigns === "true",
      stripeFields
    }).unwrap();

  const trackSuccess = () => {
    mixpanelService.track("Campaign.EditBillingInfo", {
      ...pageData.get("mixpanelData"),
      campaign_id: campaignId,
      campaign_type: campaignType
    });
    setDisplaySuccess(true);
  };

  const handleError = errorResponse => {
    const { errors, base } = errorResponse?.data || {};
    setFormErrors(errors || base || ["Unable to update payment information. Please try again."]);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setFormErrors([]);
    try {
      await submitPaymentChange();
      trackSuccess();
    } catch (errorResponse) {
      handleError(errorResponse);
    }
  };

  const renderForm = stripeReady => (
    <Form onSubmit={handleSubmit}>
      <ModalBody className="px-4 py-0">
        {displaySuccess ? (
          <div className="notifications success mt-5 mb-5">
            Payment information successfully updated.
          </div>
        ) : (
          <>
            {formErrors.length > 0 && <Alert color="danger">{formErrors.join(", ")}</Alert>}
            <Alert color="danger" className="mb-0">
              <strong>NOTE:</strong> This will update the billing info used for recurring payments.
            </Alert>
            <PaymentInformationSection
              images={images}
              hideExistingCards
              updateStateCallback={updateFormData}
              errorData={formErrors}
              showClientAdminPaymentWarning={false}
              showInvoicePurchasing={false}
              hideCreditCardPurchasing={false}
              accountholderName=""
              stripeRetailEnabled={stripeRetailEnabled}
              stripeReady={stripeReady}
            />
            <FormGroup className="mt-2">
              <Label for="apply_to_all_active_campaigns">Apply to all active campaigns?</Label>
              <Input
                type="select"
                id="apply_to_all_active_campaigns"
                value={formData.applyToAllActiveCampaigns}
                onChange={e => updateFormData({ applyToAllActiveCampaigns: e.target.value })}
              >
                <option value="false">No, only this campaign</option>
                <option value="true">Yes, apply to all campaigns</option>
              </Input>
            </FormGroup>
          </>
        )}
      </ModalBody>
      <ModalFooter className="pt-2 px-4">
        <Button color="secondary" onClick={closeModal} className="btn-sharp">
          {displaySuccess ? "Close" : "Cancel"}
        </Button>
        {!displaySuccess &&
          (stripeRetailEnabled && stripeReady ? (
            <StripeModalSubmitButton
              label="Save Changes"
              isProcessing={isLoading}
              submit={submitPaymentChange}
              onComplete={trackSuccess}
              onError={handleError}
            />
          ) : (
            <Button
              color="primary"
              type="submit"
              className="btn-sharp"
              disabled={isLoading || (stripeRetailEnabled && !stripeReady)}
            >
              {isLoading ? (
                <>
                  <Spinner color="light" size="sm" /> Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          ))}
      </ModalFooter>
    </Form>
  );

  return (
    <Modal isOpen={isOpen} toggle={closeModal} fade={false} className="account-app purchase-modal">
      <ModalHeader toggle={closeModal} className="p-2" />
      <StripeElementsProvider
        enabled={stripeRetailEnabled}
        publishableKey={stripePublishableKey}
        amount={0}
        customerSessionPath={customerSessionPath}
        paymentMethodsConfigId={stripePaymentMethodsConfigId}
      >
        {renderForm}
      </StripeElementsProvider>
    </Modal>
  );
};

EditBillingInfoModal.propTypes = propTypes;

export default EditBillingInfoModal;
